import type { IDecision } from './types.js';

/**
 * The seven locked decisions from PRODOPSAI-410's Shared Technical Context.
 *
 * Order matches the rendering order on the Calculation Reference admin page.
 * Each entry is an authored statement plus the rationale for why the decision
 * was made — sourced verbatim (or near-verbatim) from the epic.
 */
export const DECISIONS: ReadonlyArray<IDecision> = [
  {
    decisionKey: 'issue-type-scope',
    title: 'Issue-type scope is `Story / Bug / Task` only',
    statement:
      'Sub-task / Subtask / Epic / Spike / Initiative / New Demand / Incident / Data Product / Feature are excluded from every WIP / Open / Total / Cancelled metric.',
    rationale:
      'Skystone’s headline numbers must agree on what counts as "work." Including epics or sub-tasks double-counts and explodes the WIP number. Hardcoded inside repository methods so callers cannot reintroduce the wrong scope via a runtime parameter.',
  },
  {
    decisionKey: 'as-of-date-rule',
    title: '`asOfDate = min(today, period.to ?? today)`',
    statement:
      'The repository method computes `asOfDate` itself from the supplied period; callers never pass it directly.',
    rationale:
      'When a user selects a historical period, "WIP" must reflect the end of that period (`period.to`); otherwise it must reflect "now" (`today`). Hiding the rule inside the method prevents callers from reinventing it.',
  },
  {
    decisionKey: 'archived-excluded',
    title: '`archived = false` on every method',
    statement: 'Every canonical method excludes archived rows from the result set.',
    rationale:
      'Archived issues should not influence any user-facing number. Codified per-method to avoid relying on a downstream join to filter them out.',
  },
  {
    decisionKey: 'first-in-progress-as-age-source',
    title: '`first_in_progress_date` is the only WIP age source',
    statement:
      '`days_in_progress` is **never** used as a filter or as the age source. It remains in the schema for cycle-time-on-completion semantics.',
    rationale:
      '`days_in_progress` is NULL for ~97% of currently-in-progress issues (US1 finding). Computing age from it produces nonsensical results. `first_in_progress_date` is populated for all in-progress rows that ever had a status transition.',
  },
  {
    decisionKey: 'raw-table-immutability',
    title: '`raw_fact_issue_in_progress_daily` is a Fabric mirror — never extended',
    statement:
      'No new columns are added to the raw fact tables. Derivations happen at query time via JOIN, or in postgres views (`v_*` pattern) when reuse warrants.',
    rationale:
      'The raw tables are mirrored from Microsoft Fabric (Gold layer). Adding columns would diverge the mirror from the source-of-truth and break the sync pipeline.',
  },
  {
    decisionKey: 'single-method-per-concept',
    title: 'One canonical method per concept',
    statement:
      'Each concept (WIP, Open, WIP per team, etc.) is exposed by exactly one repository method. No "convenience wrappers" that re-implement the same filter elsewhere are permitted.',
    rationale:
      'The audit found four different code paths producing four different WIP numbers (47 / 788 / 18 678 / etc.) for the same scope. A single method per concept makes the canonical filter visible and auditable.',
  },
  {
    decisionKey: 'cancelled-separation',
    title: 'Cancelled is separated from Open and from Done',
    statement: 'Cancelled / Won’t Do is its own concept and never folded into either Open or Done.',
    rationale:
      'Lumping Cancelled into Open inflates burndown; folding it into Done overstates throughput. The work was not completed and is no longer outstanding — it deserves its own bucket.',
  },
] as const;
